/**
 * Iter 233 — Badge "Apporté par [Partenaire]" sur fiche prospect/opportunité.
 *
 * Affiche un visuel discret signalant l'attribution partenaire d'un lead, avec
 * tooltip détaillant la commission et le lien associé. Cliquable pour ouvrir un
 * panneau d'attribution (admin seulement) permettant la réattribution avec audit.
 *
 * Props:
 *   attribution: { partner_type, partner_id, partner_name, commission_pct, attributed_at, partner_link_name? }
 *   compact?: boolean — version mini-badge pour listes
 *   onEdit?: () => void — callback admin pour ouvrir le panneau d'édition
 */
import { Handshake, Lock } from "lucide-react";

export default function PartnerBadge({ attribution, compact = false, onEdit = null }) {
  if (!attribution || !attribution.partner_id) return null;

  const typeLabel = {
    tenant: "Centre partenaire",
    external: "Apporteur d'affaires",
    internal_user: "Commercial interne",
  }[attribution.partner_type] || attribution.partner_type;

  const dateStr = attribution.attributed_at
    ? new Date(attribution.attributed_at).toLocaleDateString("fr-FR")
    : "";

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 text-[10px] font-medium"
        title={`${typeLabel} : ${attribution.partner_name} (${attribution.commission_pct}%)`}
        data-testid="partner-badge-compact"
      >
        <Handshake className="w-3 h-3" />
        {attribution.partner_name}
      </span>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-xl p-4"
      data-testid="partner-badge"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
            <Handshake className="w-5 h-5 text-violet-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-violet-400 font-semibold">{typeLabel}</span>
              {attribution.locked && (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                  <Lock className="w-3 h-3" /> Verrouillé
                </span>
              )}
            </div>
            <div className="text-sm font-semibold text-slate-100" data-testid="partner-badge-name">
              {attribution.partner_name}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Commission {" "}
              <span className="text-amber-400 font-mono font-semibold">{attribution.commission_pct}%</span>
              {" "} à la signature/paiement
              {dateStr && <span className="text-slate-500"> · attribué le {dateStr}</span>}
            </div>
            {attribution.partner_link_name && (
              <div className="text-[11px] text-slate-500 mt-0.5 italic">
                via lien « {attribution.partner_link_name} »
              </div>
            )}
          </div>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="px-3 py-1 text-xs bg-white/5 hover:bg-white/10 rounded-lg text-slate-300"
            data-testid="partner-badge-edit-btn"
          >
            Modifier
          </button>
        )}
      </div>

      {Array.isArray(attribution.audit_log) && attribution.audit_log.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
            Historique d'attribution ({attribution.audit_log.length})
          </summary>
          <div className="mt-2 space-y-1.5 pl-3 border-l border-violet-500/20">
            {attribution.audit_log.map((a, i) => (
              <div key={i} className="text-slate-400" data-testid={`partner-audit-${i}`}>
                <span className="text-violet-400 capitalize">{a.action}</span>
                {a.from && <> · de <span className="font-mono">{a.from}</span></>}
                {a.to && <> · vers <span className="font-mono">{a.to}</span></>}
                <span className="text-slate-500"> · par {a.by_name || a.by} · {(a.at || "").slice(0, 10)}</span>
                {a.reason && <div className="text-slate-500 italic ml-3">« {a.reason} »</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
